package com.fantasytenis.web

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.screens.auth.LoginScreen
import com.fantasytenis.web.screens.auth.RegisterScreen
import com.fantasytenis.web.screens.auth.UsernameSetupScreen
import com.fantasytenis.web.screens.home.HomeScreen
import com.fantasytenis.web.screens.bracket.BracketChallengeListScreen
import com.fantasytenis.web.screens.bracket.BracketChallengeDetailScreen
import com.fantasytenis.web.screens.bracket.BracketBuilderScreenV2
import com.fantasytenis.web.screens.bracket.BracketLeaderboardScreen
import com.fantasytenis.web.screens.contests.ContestListScreen
import com.fantasytenis.web.screens.contests.ContestDetailScreen
import com.fantasytenis.web.screens.contests.ContestLeaderboardScreen
import com.fantasytenis.web.screens.contests.ContestEntryViewScreen
import com.fantasytenis.web.screens.survivor.SurvivorContestListScreen
import com.fantasytenis.web.screens.survivor.SurvivorContestDetailScreen
import com.fantasytenis.web.screens.survivor.SurvivorPickSelectionScreen
import com.fantasytenis.web.screens.survivor.SurvivorEntryViewScreen
import com.fantasytenis.web.screens.survivor.SurvivorLeaderboardScreen
import com.fantasytenis.web.screens.live.LiveScoringScreen
import com.fantasytenis.web.screens.live.LiveMatchDetailScreen
import com.fantasytenis.web.screens.gameday.GameDayScreen
import com.fantasytenis.web.screens.lineup.LineupBuilderScreen
import com.fantasytenis.web.screens.admin.AdminScreen
import com.fantasytenis.web.screens.profile.ProfileScreen
import com.fantasytenis.web.screens.settings.AccountSettingsScreen
import com.fantasytenis.web.screens.skills.SkillRatingsHubScreen
import com.fantasytenis.web.screens.skills.CategoryLeaderboardScreen
import com.fantasytenis.web.screens.skills.PlayerSkillDetailScreen
import com.fantasytenis.web.components.FirstTimeUserModal
import com.fantasytenis.web.utils.WebPreferences
import com.fantasytenis.web.theme.FantasyTennisTheme
import com.fantasytenis.web.theme.ThemeState
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable

fun main() {
    // Initialize navigation with browser history support
    NavigationState.initialize()

    renderComposable(rootElementId = "root") {
        FantasyTennisApp()
    }
}

@Composable
fun FantasyTennisApp() {
    val isLoggedIn by SharedData.loggedIn.collectAsState()
    val currentUser by SharedData.currentUser.collectAsState()
    // Initialize with the current route from URL (parsed during NavigationState.initialize())
    var currentRoute by remember { mutableStateOf(NavigationState.getCurrentRoute()) }
    // Track if we're still checking for stored credentials
    var isCheckingAuth by remember { mutableStateOf(true) }
    // Track if user needs username setup (existing user without username)
    val needsUsernameSetup = isLoggedIn && currentUser != null && currentUser?.username == null

    // First-time user onboarding modal state
    var showOnboarding by remember { mutableStateOf(false) }

    // Check if we need to show onboarding after auth is confirmed
    LaunchedEffect(isLoggedIn, isCheckingAuth) {
        if (isLoggedIn && !isCheckingAuth && !WebPreferences.hasCompletedOnboarding()) {
            showOnboarding = true
        }
    }

    // Listen to navigation changes (including browser back/forward)
    LaunchedEffect(Unit) {
        NavigationState.addListener { route ->
            currentRoute = route
        }
    }

    // Check for stored credentials on startup
    LaunchedEffect(Unit) {
        // Fetch platform config (fire and forget - don't block on this)
        try {
            DependencyContainer.getPlatformConfigUseCase.invoke()
        } catch (e: Exception) {
            // Config fetch failed, continue anyway
        }

        val hasCredentials = DependencyContainer.sharedPrefs.hasCredentials()
        if (hasCredentials) {
            // Try to restore session
            DependencyContainer.getCurrentUserUseCase()
                .onSuccess { user ->
                    SharedData.currentUser.value = user
                    SharedData.loggedIn.value = true
                }
                .onFailure {
                    // Token expired, redirect to login
                    DependencyContainer.sharedPrefs.clear()
                    NavigationState.navigate(Route.Login)
                }
        }
        isCheckingAuth = false
    }

    Style(GlobalStylesheet)

    // Inject additional CSS (keyframes, etc.) and set up viewport height
    LaunchedEffect(Unit) {
        injectGlobalCss(AdditionalCss)
        setupViewportHeight()
    }

    // Wrap app in theme provider to handle dark/light mode
    FantasyTennisTheme {
        // Get current theme state for background color
        val isDark by ThemeState.isDarkMode

        Div({
            classes(if (isDark) "app-container" else "app-container", "dark-mode-aware")
            style {
                property("min-height", "var(--app-height)")
                backgroundColor(
                    if (isDark) org.jetbrains.compose.web.css.rgb(18, 18, 18)
                    else com.fantasytenis.web.theme.Colors.Background
                )
            }
        }) {
        when {
            // Show loading while checking auth
            isCheckingAuth -> {
                AppLoadingScreen()
            }
            // Public routes (no auth required)
            !isLoggedIn && currentRoute !is Route.Register -> {
                LoginScreen(
                    onLoginSuccess = {
                        NavigationState.navigate(Route.Home)
                    },
                    onNavigateToRegister = {
                        NavigationState.navigate(Route.Register)
                    }
                )
            }
            currentRoute is Route.Register -> {
                RegisterScreen(
                    onRegisterSuccess = {
                        NavigationState.navigate(Route.Home)
                    },
                    onNavigateToLogin = {
                        NavigationState.navigate(Route.Login)
                    }
                )
            }
            // Existing user without username - show setup screen
            needsUsernameSetup -> {
                UsernameSetupScreen(
                    onComplete = {
                        // Username set, proceed to intended route
                        NavigationState.navigate(currentRoute, pushState = false)
                    }
                )
            }
            // Authenticated routes
            else -> {
                when (currentRoute) {
                    // Bracket Challenge routes
                    is Route.BracketChallenges -> {
                        BracketChallengeListScreen()
                    }
                    is Route.BracketChallengeDetail -> {
                        val route = currentRoute as Route.BracketChallengeDetail
                        BracketChallengeDetailScreen(challengeId = route.challengeId)
                    }
                    is Route.BracketBuilder -> {
                        val route = currentRoute as Route.BracketBuilder
                        BracketBuilderScreenV2(
                            challengeId = route.challengeId,
                            entryId = route.entryId
                        )
                    }
                    is Route.BracketLeaderboard -> {
                        val route = currentRoute as Route.BracketLeaderboard
                        BracketLeaderboardScreen(challengeId = route.challengeId)
                    }
                    // Contest routes
                    is Route.Contests -> {
                        ContestListScreen()
                    }
                    is Route.ContestDetail -> {
                        val route = currentRoute as Route.ContestDetail
                        ContestDetailScreen(contestId = route.contestId)
                    }
                    is Route.ContestLeaderboard -> {
                        val route = currentRoute as Route.ContestLeaderboard
                        ContestLeaderboardScreen(contestId = route.contestId)
                    }
                    is Route.ContestEntryView -> {
                        val route = currentRoute as Route.ContestEntryView
                        ContestEntryViewScreen(
                            contestId = route.contestId,
                            entryId = route.entryId
                        )
                    }
                    is Route.Lineup -> {
                        val route = currentRoute as Route.Lineup
                        LineupBuilderScreen(
                            contestId = route.contestId,
                            entryId = route.entryId,
                            lineupId = route.lineupId,
                            onSubmit = { NavigationState.navigate(Route.ContestDetail(route.contestId)) },
                            onBack = { NavigationState.goBack() }
                        )
                    }
                    // Game Day live experience
                    is Route.GameDay -> {
                        val route = currentRoute as Route.GameDay
                        GameDayScreen(
                            contestId = route.contestId,
                            entryId = route.entryId
                        )
                    }
                    // Survivor routes
                    is Route.SurvivorContests -> {
                        SurvivorContestListScreen()
                    }
                    is Route.SurvivorContestDetail -> {
                        val route = currentRoute as Route.SurvivorContestDetail
                        SurvivorContestDetailScreen(contestId = route.contestId)
                    }
                    is Route.SurvivorPickSelection -> {
                        val route = currentRoute as Route.SurvivorPickSelection
                        SurvivorPickSelectionScreen(
                            contestId = route.contestId,
                            entryId = route.entryId
                        )
                    }
                    is Route.SurvivorEntry -> {
                        val route = currentRoute as Route.SurvivorEntry
                        SurvivorEntryViewScreen(
                            contestId = route.contestId,
                            entryId = route.entryId
                        )
                    }
                    is Route.SurvivorLeaderboard -> {
                        val route = currentRoute as Route.SurvivorLeaderboard
                        SurvivorLeaderboardScreen(contestId = route.contestId)
                    }
                    // Match Center routes
                    is Route.Matches -> {
                        LiveScoringScreen()
                    }
                    is Route.MatchDetail -> {
                        val route = currentRoute as Route.MatchDetail
                        LiveMatchDetailScreen(matchId = route.matchId)
                    }
                    // My Entries - currently shows Home which has entries section
                    is Route.MyEntries -> {
                        HomeScreen()
                    }
                    // Profile - view profile
                    is Route.Profile -> {
                        ProfileScreen(
                            onNavigateToSettings = { NavigationState.navigate(Route.Settings) }
                        )
                    }
                    // Account Settings - edit profile
                    is Route.Settings -> {
                        AccountSettingsScreen(
                            onNavigateBack = { NavigationState.goBack() }
                        )
                    }
                    // Admin Panel
                    is Route.Admin -> {
                        AdminScreen()
                    }
                    // Skills routes
                    is Route.Skills -> {
                        SkillRatingsHubScreen()
                    }
                    is Route.SkillLeaderboard -> {
                        val route = currentRoute as Route.SkillLeaderboard
                        CategoryLeaderboardScreen(categoryName = route.category)
                    }
                    is Route.PlayerSkillDetail -> {
                        val route = currentRoute as Route.PlayerSkillDetail
                        PlayerSkillDetailScreen(playerId = route.playerId)
                    }
                    // Default to Home
                    else -> {
                        HomeScreen()
                    }
                }

                // First-time user onboarding modal (shown once after login)
                if (showOnboarding) {
                    FirstTimeUserModal(
                        onComplete = {
                            showOnboarding = false
                        }
                    )
                }
            }
        }
        }
    }
}

/**
 * Loading screen shown while checking authentication state.
 * Matches the loading UI from index.html for a seamless transition.
 */
@Composable
private fun AppLoadingScreen() {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("min-height", "var(--app-height)")
            gap(24.px)
            property("background", "linear-gradient(135deg, rgb(245, 247, 250) 0%, rgb(232, 245, 233) 50%, rgb(245, 247, 250) 100%)")
        }
    }) {
        // Logo box with tennis ball icon
        Div({
            style {
                width(72.px)
                height(72.px)
                backgroundColor(com.fantasytenis.web.theme.Colors.Primary)
                borderRadius(16.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("box-shadow", "0 4px 6px -1px rgba(0, 166, 81, 0.2)")
            }
        }) {
            // Simple tennis ball representation
            Div({
                style {
                    width(36.px)
                    height(36.px)
                    borderRadius(50.percent)
                    border(2.px, LineStyle.Solid, Color.white)
                    position(Position.Relative)
                }
            }) {
                // Vertical seam
                Div({
                    style {
                        position(Position.Absolute)
                        width(2.px)
                        height(100.percent)
                        left(50.percent)
                        property("transform", "translateX(-50%)")
                        backgroundColor(Color.white)
                    }
                })
            }
        }
        // Title and subtitle
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Span({
                style {
                    fontSize(24.px)
                    fontWeight(700)
                    color(com.fantasytenis.web.theme.Colors.TextPrimary)
                }
            }) {
                Text("Baseline")
            }
            Span({
                style {
                    fontSize(14.px)
                    color(com.fantasytenis.web.theme.Colors.TextSecondary)
                }
            }) {
                Text("Loading...")
            }
        }
        // Spinner
        Div({
            style {
                width(24.px)
                height(24.px)
                border(2.px, LineStyle.Solid, com.fantasytenis.web.theme.Colors.BorderLight)
                property("border-top-color", "rgb(0, 166, 81)")
                borderRadius(50.percent)
                property("animation", "spin 0.8s linear infinite")
            }
        })
    }
}

object GlobalStylesheet : StyleSheet() {
    init {
        "*" style {
            margin(0.px)
            padding(0.px)
            property("box-sizing", "border-box")
        }

        "body" style {
            fontFamily(com.fantasytenis.web.theme.Typography.FontFamily.Primary)
            fontSize(16.px)
            lineHeight("1.5")
            color(com.fantasytenis.web.theme.Colors.TextPrimary)
            backgroundColor(com.fantasytenis.web.theme.Colors.Background)
        }

        "a" style {
            textDecoration("none")
            color(com.fantasytenis.web.theme.Colors.Accent)
        }

        "button" style {
            cursor("pointer")
            border(0.px)
            property("outline", "none")
        }

        "input" style {
            border(0.px)
            property("outline", "none")
        }
    }
}

/**
 * Additional CSS that can't be expressed via StyleSheet API.
 * Includes keyframe animations, focus states, and hover effects.
 */
const val AdditionalCss = """
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

@keyframes slideInFromLeft {
    from { transform: translateX(-20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
}

@keyframes slideInFromRight {
    from { transform: translateX(20px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
}

@keyframes pickFlash {
    0% { background-color: rgba(0, 166, 81, 0.3); }
    100% { background-color: rgba(0, 166, 81, 0.1); }
}

@keyframes connectorDraw {
    from { stroke-dashoffset: 200; }
    to { stroke-dashoffset: 0; }
}

@keyframes bounceIn {
    0% { transform: scale(0.9); opacity: 0; }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); opacity: 1; }
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

/* ========================================
   GAME DAY LIVE ANIMATIONS
   ======================================== */

/* Score increase - green glow + scale */
@keyframes scoreUp {
    0% { transform: scale(1); }
    50% { transform: scale(1.15); box-shadow: 0 0 20px rgba(76, 175, 80, 0.6); }
    100% { transform: scale(1); }
}

/* Score decrease - red flash + shake */
@keyframes scoreDown {
    0%, 100% { transform: translateX(0); }
    10% { transform: translateX(-4px); background-color: rgba(244, 67, 54, 0.15); }
    30% { transform: translateX(4px); }
    50% { transform: translateX(-4px); }
    70% { transform: translateX(4px); }
    90% { transform: translateX(-4px); }
}

/* New event slides in from top */
@keyframes eventSlideIn {
    0% { transform: translateY(-20px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
}

/* Rank improvement - bounce up */
@keyframes rankUp {
    0%, 100% { transform: translateY(0); }
    30% { transform: translateY(-8px); }
    50% { transform: translateY(-4px); }
    70% { transform: translateY(-6px); }
}

/* Rank drop - subtle drop */
@keyframes rankDown {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(4px); }
}

/* Major event - radial burst (ace, break point converted) */
@keyframes majorEvent {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.7); }
    50% { transform: scale(1.1); box-shadow: 0 0 0 15px rgba(255, 215, 0, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 215, 0, 0); }
}

/* Live indicator pulse */
@keyframes livePulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(0.9); }
}

/* Point badge float up and fade */
@keyframes pointFloatUp {
    0% { transform: translateY(0); opacity: 1; }
    100% { transform: translateY(-30px); opacity: 0; }
}

/* Score delta badge pop */
@keyframes deltaBadgePop {
    0% { transform: scale(0); opacity: 0; }
    60% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
}

/* Celebration confetti particle */
@keyframes confettiFall {
    0% { transform: translateY(-100%) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
}

/* Payout zone celebration */
@keyframes payoutCelebration {
    0% { background-color: rgba(76, 175, 80, 0); }
    25% { background-color: rgba(76, 175, 80, 0.2); }
    50% { background-color: rgba(255, 215, 0, 0.2); }
    75% { background-color: rgba(76, 175, 80, 0.2); }
    100% { background-color: rgba(76, 175, 80, 0); }
}

/* Category slot glow for active match */
@keyframes categoryGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(0, 166, 81, 0.4); }
    50% { box-shadow: 0 0 12px 2px rgba(0, 166, 81, 0.6); }
}

/* Game Day Component Classes */
.game-day-score-up {
    animation: scoreUp 0.5s ease-out;
}

.game-day-score-down {
    animation: scoreDown 0.4s ease-out;
}

.game-day-event-new {
    animation: eventSlideIn 0.3s ease-out;
}

.game-day-rank-up {
    animation: rankUp 0.5s ease-out;
}

.game-day-rank-down {
    animation: rankDown 0.4s ease-out;
}

.game-day-major-event {
    animation: majorEvent 0.6s ease-out;
}

.game-day-live-indicator {
    animation: livePulse 1.5s ease-in-out infinite;
}

.game-day-delta-badge {
    animation: deltaBadgePop 0.3s ease-out;
}

.game-day-point-float {
    animation: pointFloatUp 1s ease-out forwards;
}

.game-day-payout-celebration {
    animation: payoutCelebration 1s ease-out;
}

.game-day-category-active {
    animation: categoryGlow 2s ease-in-out infinite;
}

/* ========================================
   SWEAT METER ANIMATIONS
   ======================================== */

@keyframes sweatPulse {
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    50% {
        transform: scale(1.01);
        box-shadow: 0 4px 16px rgba(244, 67, 54, 0.3);
    }
}

.sweat-meter-pulsing {
    animation: sweatPulse 1s ease-in-out infinite;
}

/* ========================================
   LEADERBOARD RANK CHANGE ANIMATIONS
   ======================================== */

.leaderboard-rank-up {
    animation: leaderboardRankUp 0.6s ease-out;
}

.leaderboard-rank-down {
    animation: leaderboardRankDown 0.6s ease-out;
}

@keyframes leaderboardRankUp {
    0% {
        transform: translateY(0);
        background-color: transparent;
    }
    30% {
        transform: translateY(-4px);
        background-color: rgba(76, 175, 80, 0.2);
    }
    100% {
        transform: translateY(0);
        background-color: rgba(76, 175, 80, 0.15);
    }
}

@keyframes leaderboardRankDown {
    0% {
        transform: translateY(0);
        background-color: transparent;
    }
    30% {
        transform: translateY(4px);
        background-color: rgba(244, 67, 54, 0.2);
    }
    100% {
        transform: translateY(0);
        background-color: rgba(244, 67, 54, 0.15);
    }
}

/* ========================================
   CELEBRATION OVERLAY ANIMATIONS
   ======================================== */

@keyframes celebrationBounce {
    0% { transform: scale(0.5); opacity: 0; }
    50% { transform: scale(1.2); }
    70% { transform: scale(0.9); }
    100% { transform: scale(1); opacity: 1; }
}

@keyframes celebrationSlideUp {
    0% { transform: translateY(20px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
}

@keyframes celebrationFadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
}

@keyframes tooltipFadeIn {
    0% { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
}

.celebration-overlay {
    opacity: 0;
    transition: opacity 0.3s ease;
}

.celebration-overlay-visible {
    opacity: 1;
}

.celebration-overlay-exiting {
    opacity: 0;
}

.celebration-toast {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
    transition: all 0.3s ease;
}

.celebration-toast-visible {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
}

/* Event type colors */
.event-positive {
    background-color: rgba(76, 175, 80, 0.1);
    border-left: 3px solid rgb(76, 175, 80);
}

.event-negative {
    background-color: rgba(244, 67, 54, 0.1);
    border-left: 3px solid rgb(244, 67, 54);
}

.event-major {
    background-color: rgba(255, 215, 0, 0.15);
    border-left: 3px solid rgb(255, 215, 0);
}

/* Category colors */
.category-serve { border-color: rgb(33, 150, 243); }
.category-forehand { border-color: rgb(76, 175, 80); }
.category-backhand { border-color: rgb(244, 67, 54); }
.category-volleys { border-color: rgb(156, 39, 176); }
.category-fighting { border-color: rgb(255, 152, 0); }

.category-serve-bg { background-color: rgba(33, 150, 243, 0.1); }
.category-forehand-bg { background-color: rgba(76, 175, 80, 0.1); }
.category-backhand-bg { background-color: rgba(244, 67, 54, 0.1); }
.category-volleys-bg { background-color: rgba(156, 39, 176, 0.1); }
.category-fighting-bg { background-color: rgba(255, 152, 0, 0.1); }

/* Game Day responsive */
@media (max-width: 768px) {
    .game-day-lineup-grid {
        grid-template-columns: 1fr !important;
    }

    .game-day-event-feed {
        max-height: 200px !important;
    }

    .game-day-leaderboard-context {
        flex-direction: column !important;
        gap: 8px !important;
    }
}

@media (max-width: 480px) {
    .game-day-player-card {
        padding: 12px !important;
    }

    .game-day-category-breakdown {
        font-size: 13px !important;
    }
}

/* Welcome Modal Animations */
@keyframes modalSlideIn {
    0% { transform: translateY(20px) scale(0.95); opacity: 0; }
    100% { transform: translateY(0) scale(1); opacity: 1; }
}

@keyframes flamePulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
}

/* Streak Badge Animation */
@keyframes streakGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4); }
    50% { box-shadow: 0 0 12px 4px rgba(255, 152, 0, 0.3); }
}

.streak-badge-animated {
    animation: streakGlow 2s ease-in-out infinite;
}

/* Burst particle animation for celebrations */
@keyframes burstOut {
    0% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
    }
    100% {
        transform: translate(calc(-50% + var(--end-x)), calc(-50% + var(--end-y))) scale(0);
        opacity: 0;
    }
}

/* Player selection slot fill animation */
@keyframes slotFill {
    0% { transform: scale(1); }
    40% { transform: scale(1.05); }
    100% { transform: scale(1); }
}

.slot-fill-animation {
    animation: slotFill 0.4s ease-out;
}

/* Checkmark pop animation */
@keyframes checkmarkPop {
    0% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
    60% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
    100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}

/* Salary cap tension animations */
@keyframes budgetPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
}

@keyframes budgetShake {
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-3px); }
    40% { transform: translateX(3px); }
    60% { transform: translateX(-3px); }
    80% { transform: translateX(3px); }
}

.budget-tension-high {
    animation: budgetPulse 0.8s ease-in-out infinite;
}

.budget-over {
    animation: budgetShake 0.4s ease-out;
}

/* Success toast slide animation */
@keyframes toastSlideIn {
    0% { transform: translateY(-20px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
}

.toast-slide-in {
    animation: toastSlideIn 0.3s ease-out;
}

/* Bracket Tree Styles */
.bracket-match-node {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bracket-match-node:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.bracket-player-row {
    transition: background-color 0.15s ease, border-color 0.15s ease;
}

.bracket-player-row:hover:not(.selected):not(.view-only) {
    background-color: rgba(0, 166, 81, 0.05) !important;
}

.bracket-player-row.selected {
    animation: pickFlash 0.3s ease-out;
}

.bracket-connector-line {
    transition: stroke 0.2s ease, stroke-width 0.2s ease;
}

.bracket-connector-line.highlighted {
    stroke: rgb(0, 166, 81) !important;
    stroke-width: 3 !important;
}

.connector-animated {
    stroke-dasharray: 200;
    stroke-dashoffset: 200;
    animation: connectorDraw 0.5s ease-out forwards;
}

/* Bird's Eye View */
.bird-eye-container {
    animation: fadeIn 0.3s ease-out;
}

.bird-eye-mini-bracket {
    transition: transform 0.3s ease;
    cursor: grab;
}

.bird-eye-mini-bracket:active {
    cursor: grabbing;
}

/* Pick Animations */
.pick-checkmark {
    animation: bounceIn 0.3s ease-out;
}

/* Round Navigator */
.round-tab {
    transition: all 0.2s ease;
}

.round-tab:hover {
    transform: scale(1.02);
}

.round-tab.active {
    transform: scale(1.05);
}

/* Save Status Indicator */
.save-status-indicator {
    transition: opacity 0.3s ease;
}

.save-status-indicator.saving {
    animation: pulse 1s infinite;
}

/* Modal Animations */
.modal-overlay {
    animation: fadeIn 0.2s ease-out;
}

.modal-content {
    animation: bounceIn 0.3s ease-out;
}

/* Cascade Confirmation */
.cascade-warning-icon {
    animation: pulse 1.5s ease-in-out infinite;
}

/* Progress Bar Animation */
.progress-fill {
    transition: width 0.3s ease-out;
}

/* Keyboard Focus Styles */
.bracket-match-node:focus-visible {
    outline: 3px solid rgba(0, 166, 81, 0.5);
    outline-offset: 2px;
}

.bracket-player-row:focus-visible {
    outline: 2px solid rgb(0, 166, 81);
    outline-offset: 1px;
}

/* Mobile Touch Feedback */
@media (hover: none) {
    .bracket-player-row:active:not(.selected):not(.view-only) {
        background-color: rgba(0, 166, 81, 0.1) !important;
        transform: scale(0.98);
    }

    .bracket-match-node:active {
        transform: scale(0.98);
    }
}

/* Responsive Bracket Styles */
@media (max-width: 768px) {
    .bracket-match-node {
        min-width: 160px !important;
    }

    .bird-eye-container {
        padding: 12px !important;
    }
}

/* Input focus states - WCAG 2.4.7 compliant */
.baseline-input:focus {
    border-color: rgb(0, 166, 81) !important;
    box-shadow: 0 0 0 3px rgba(0, 166, 81, 0.2) !important;
    outline: none;
}

.baseline-input--error:focus {
    border-color: rgb(244, 67, 54) !important;
    box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.2) !important;
}

/* Button focus and hover states */
.baseline-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 166, 81, 0.3);
}

.baseline-button--primary:hover:not(:disabled) {
    background-color: rgb(0, 128, 63) !important;
}

.baseline-button--secondary:hover:not(:disabled) {
    background-color: rgb(240, 240, 240) !important;
}

.baseline-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

/* Card hover effects */
.baseline-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Avatar hover effects */
.avatar-clickable:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Skeleton loading animation */
.skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
}

/* Modal backdrop */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

/* Focus trap for modals */
.modal-content:focus {
    outline: none;
}

/* CSS Custom Properties for theming */
:root {
    --bg-primary: rgb(250, 250, 250);
    --bg-surface: rgb(255, 255, 255);
    --bg-surface-variant: rgb(245, 245, 245);
    --text-primary: rgb(33, 33, 33);
    --text-secondary: rgb(117, 117, 117);
    --border-color: rgb(224, 224, 224);
    --card-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    --card-shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.15);
    --primary-color: rgb(0, 166, 81);
    --primary-light: rgb(76, 196, 133);
    --accent-color: rgb(33, 150, 243);
}

body.dark-theme {
    --bg-primary: rgb(18, 18, 18);
    --bg-surface: rgb(30, 30, 30);
    --bg-surface-variant: rgb(45, 45, 45);
    --text-primary: rgb(255, 255, 255);
    --text-secondary: rgb(179, 179, 179);
    --border-color: rgb(66, 66, 66);
    --card-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    --card-shadow-hover: 0 4px 12px rgba(0, 0, 0, 0.5);
    --primary-color: rgb(76, 196, 133);
    --primary-light: rgb(129, 216, 168);
    --accent-color: rgb(100, 181, 246);
}

/* Dark theme overrides using !important to override inline styles */
body.dark-theme {
    background-color: var(--bg-primary) !important;
    color: var(--text-primary) !important;
}

body.dark-theme .baseline-card {
    background-color: var(--bg-surface) !important;
    border-color: var(--border-color) !important;
    box-shadow: var(--card-shadow) !important;
}

body.dark-theme .baseline-card:hover {
    box-shadow: var(--card-shadow-hover) !important;
}

body.dark-theme .baseline-input {
    background-color: var(--bg-surface-variant) !important;
    color: var(--text-primary) !important;
    border-color: var(--border-color) !important;
}

body.dark-theme .baseline-input:focus {
    border-color: var(--primary-light) !important;
    box-shadow: 0 0 0 3px rgba(76, 196, 133, 0.2) !important;
}

body.dark-theme .baseline-input::placeholder {
    color: rgb(128, 128, 128) !important;
}

body.dark-theme .baseline-button--secondary {
    color: var(--primary-light) !important;
    border-color: var(--primary-light) !important;
    background-color: transparent !important;
}

body.dark-theme .baseline-button--secondary:hover:not(:disabled) {
    background-color: rgba(76, 196, 133, 0.15) !important;
}

body.dark-theme .skeleton {
    background: linear-gradient(90deg, #2d2d2d 25%, #3d3d3d 50%, #2d2d2d 75%) !important;
}

body.dark-theme .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.8) !important;
}

/* Dark theme text colors */
body.dark-theme h1, body.dark-theme h2, body.dark-theme h3,
body.dark-theme h4, body.dark-theme h5, body.dark-theme h6 {
    color: var(--text-primary) !important;
}

body.dark-theme p, body.dark-theme span {
    color: var(--text-secondary);
}

body.dark-theme a {
    color: var(--accent-color) !important;
}

/* Dark theme header retains its color */
body.dark-theme header {
    background-color: rgb(0, 128, 63) !important;
}

/* Force dark background on surfaces */
body.dark-theme main,
body.dark-theme section,
body.dark-theme article,
body.dark-theme aside {
    background-color: transparent !important;
}

/* Dark theme for form containers and panels */
body.dark-theme .baseline-surface,
body.dark-theme [class*="panel"],
body.dark-theme [class*="container"] {
    background-color: var(--bg-surface) !important;
}

/* Touch feedback for mobile */
@media (hover: none) {
    .baseline-button:active:not(:disabled) {
        transform: scale(0.98);
        opacity: 0.9;
    }

    .baseline-card:active {
        transform: scale(0.98);
    }
}

/* Mobile responsive adjustments - INCREASE touch targets on mobile */
@media (max-width: 768px) {
    .baseline-button {
        min-height: 44px !important;
        padding: 12px 20px !important;
        font-size: 15px !important;
    }
}

@media (max-width: 480px) {
    .baseline-button {
        min-height: 48px !important;
        padding: 14px 20px !important;
        font-size: 14px !important;
    }
}

/* Responsive CSS Custom Properties */
:root {
    --breakpoint-sm: 480px;
    --breakpoint-md: 640px;
    --breakpoint-lg: 768px;
    --breakpoint-xl: 1024px;
    --touch-target-min: 44px;
    --spacing-page-mobile: 16px;
    --spacing-page-tablet: 20px;
    --spacing-page-desktop: 24px;
    /* Viewport height - JS will override with actual value, fallback to dvh/svh/vh */
    --app-height: 100dvh;
}

/* Fallback for browsers that don't support dvh */
@supports not (height: 100dvh) {
    :root {
        --app-height: 100vh;
    }
}

/* Entry cards responsive grid */
@media (max-width: 640px) {
    .entries-grid {
        grid-template-columns: 1fr !important;
    }

    .entry-card {
        min-width: 100% !important;
        max-width: 100% !important;
    }
}

/* Lineup builder responsive grid */
@media (max-width: 1024px) {
    .lineup-slots-grid {
        grid-template-columns: repeat(3, 1fr) !important;
    }
}

@media (max-width: 768px) {
    .lineup-slots-grid {
        grid-template-columns: repeat(2, 1fr) !important;
    }
}

@media (max-width: 480px) {
    .lineup-slots-grid {
        grid-template-columns: 1fr !important;
    }
}

/* Challenge Detail Page Styles */
.challenge-info-box {
    transition: all 0.2s ease;
}

.challenge-info-box:hover {
    border-color: rgb(0, 166, 81) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 166, 81, 0.1);
}

.challenge-entry-row {
    transition: all 0.2s ease;
}

.challenge-entry-row:hover {
    background-color: rgb(245, 245, 245) !important;
    border-color: rgb(0, 166, 81) !important;
}

.scoring-table-row {
    transition: background-color 0.15s ease;
}

.scoring-table-row:hover {
    background-color: rgba(0, 166, 81, 0.05) !important;
}

/* Challenge Detail Responsive */
@media (max-width: 640px) {
    .challenge-detail-header {
        padding: 10px 16px !important;
    }
    .challenge-detail-header h2 {
        font-size: 16px !important;
    }
    .challenge-stats-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 12px !important;
    }
    .challenge-info-card {
        padding: 20px !important;
        border-radius: 12px !important;
    }
    .challenge-main-content {
        padding: 16px 16px 32px 16px !important;
    }
}

/* ========================================
   MOBILE BRACKET BUILDER STYLES
   ======================================== */

/* Touch-optimized bracket tree container */
.bracket-tree-container {
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
}

/* Mobile bracket layout adjustments */
@media (max-width: 768px) {
    .bracket-tree-inner {
        min-width: 100vw;
    }

    .bracket-match-node {
        min-width: 150px !important;
        max-width: 180px !important;
    }

    .bracket-player-row {
        padding: 10px 12px !important;
        min-height: 44px; /* iOS minimum touch target */
    }

    /* Larger tap targets on mobile */
    .bracket-player-row button,
    .bracket-player-row [role="button"] {
        min-height: 44px;
        min-width: 44px;
    }

    /* Feeder panel mobile */
    .feeder-panel-header {
        padding: 14px 16px !important;
    }

    .feeder-panel-content {
        padding: 12px !important;
    }

    /* Simplified feeder sections on mobile */
    .feeder-expanded-content {
        max-height: 60vh !important;
    }

    /* Hybrid bracket container mobile */
    .hybrid-bracket-container .feeder-panel {
        max-height: 35vh;
    }
}

/* Small phone adjustments */
@media (max-width: 480px) {
    .bracket-match-node {
        min-width: 140px !important;
        max-width: 160px !important;
    }

    .bracket-player-row {
        padding: 8px 10px !important;
    }

    .bracket-player-row .player-name {
        font-size: 12px !important;
    }

    .bracket-player-row .seed-badge {
        font-size: 10px !important;
        padding: 2px 6px !important;
    }
}

/* Touch gesture feedback */
.bracket-tree-container.is-panning {
    cursor: grabbing !important;
}

.bracket-tree-container.is-pinching {
    touch-action: none !important;
}

/* Pinch zoom indicator */
.zoom-indicator {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 12px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 100;
}

.zoom-indicator.visible {
    opacity: 1;
}

/* Swipe navigation hints */
.swipe-hint {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
    color: rgba(0, 166, 81, 0.5);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.swipe-hint.left {
    left: 10px;
}

.swipe-hint.right {
    right: 10px;
}

.swipe-hint.visible {
    opacity: 1;
    animation: pulse 2s infinite;
}

/* Mobile-optimized bird's eye view */
@media (max-width: 768px) {
    .bird-eye-container {
        padding: 12px !important;
    }

    .bird-eye-mini-bracket {
        overflow: auto;
        -webkit-overflow-scrolling: touch;
    }

    /* Quarter buttons stacked on small screens */
    .bird-eye-container .quarter-nav {
        flex-wrap: wrap !important;
        justify-content: center !important;
    }

    .bird-eye-container .quarter-nav > div {
        flex: 0 0 45% !important;
        margin-bottom: 8px !important;
    }
}

/* Mobile header adjustments for bracket builder */
@media (max-width: 768px) {
    .bracket-builder-header {
        padding: 8px 12px !important;
        flex-wrap: wrap;
    }

    .bracket-builder-header h2 {
        font-size: 14px !important;
        order: 1;
        width: 100%;
        text-align: center;
        margin-bottom: 8px;
    }

    .bracket-builder-header .back-button {
        order: 2;
    }

    .bracket-builder-header .submit-button {
        order: 3;
    }

    .bracket-builder-header .save-status {
        order: 4;
        width: 100%;
        text-align: center;
        margin-top: 8px;
    }
}

/* Progress bar mobile */
@media (max-width: 768px) {
    .bracket-progress-bar {
        padding: 12px 16px !important;
    }

    .bracket-progress-bar .round-badges {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 8px;
    }

    .bracket-progress-bar .round-badge {
        flex-shrink: 0;
    }
}

/* View mode toggle mobile */
@media (max-width: 768px) {
    .view-mode-toolbar {
        padding: 8px 12px !important;
        justify-content: space-between !important;
    }

    .view-mode-toolbar .bird-eye-button {
        font-size: 12px !important;
        padding: 6px 10px !important;
    }
}

/* Half-view mobile navigation */
.half-view-tabs {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border-color);
    position: sticky;
    top: 0;
    z-index: 10;
}

.half-view-tab {
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
    background: var(--bg-surface-variant);
    color: var(--text-primary);
}

.half-view-tab.active {
    background: rgb(0, 166, 81);
    color: white;
}

/* Floating action button for mobile */
.bracket-fab {
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 56px;
    height: 56px;
    border-radius: 28px;
    background: rgb(0, 166, 81);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    box-shadow: 0 4px 12px rgba(0, 166, 81, 0.3);
    cursor: pointer;
    z-index: 100;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bracket-fab:active {
    transform: scale(0.95);
}

.bracket-fab:hover {
    box-shadow: 0 6px 16px rgba(0, 166, 81, 0.4);
}

@media (min-width: 769px) {
    .bracket-fab {
        display: none;
    }
}

/* Player comparison modal mobile */
@media (max-width: 768px) {
    .player-comparison-modal {
        padding: 16px !important;
        max-height: 90vh !important;
    }

    .player-comparison-modal .grid {
        grid-template-columns: 1fr !important;
        gap: 16px !important;
    }

    .player-comparison-modal .vs {
        display: none !important;
    }

    .player-detail-card {
        padding: 16px !important;
    }
}

/* Cascade confirmation dialog mobile */
@media (max-width: 480px) {
    .cascade-dialog {
        width: 95% !important;
        max-width: none !important;
        margin: 0 auto !important;
    }

    .cascade-dialog .buttons {
        flex-direction: column !important;
        gap: 8px !important;
    }

    .cascade-dialog .buttons button {
        width: 100% !important;
    }
}

/* Landscape mode optimizations */
@media (max-height: 500px) and (orientation: landscape) {
    .bracket-builder-header {
        padding: 6px 12px !important;
    }

    .bracket-progress-bar {
        padding: 8px 12px !important;
    }

    .view-mode-toolbar {
        padding: 6px 12px !important;
    }

    .bracket-tree-container {
        height: calc(var(--app-height) - 120px) !important;
    }
}

/* Skill drawer resize styles */
body.is-resizing-panel {
    cursor: ew-resize !important;
    user-select: none !important;
    -webkit-user-select: none !important;
}

body.is-resizing-panel * {
    cursor: ew-resize !important;
}

.skill-drawer-content.is-resizing {
    transition: none !important;
}

/* Hide resize handle on mobile */
@media (max-width: 768px) {
    .resize-handle {
        display: none !important;
    }
}

/* Info icon button styles */
.info-icon-button {
    transition: transform 0.2s ease, background-color 0.2s ease;
}

.info-icon-button:hover {
    transform: scale(1.05);
}

.info-icon-button:active {
    transform: scale(0.95);
}

/* Mobile Slot Carousel - hide scrollbar while maintaining scroll functionality */
.mobile-slot-carousel::-webkit-scrollbar {
    display: none;
}

.mobile-slot-carousel {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

/* Mobile touch feedback for interactive elements */
@media (hover: none) {
    .mobile-slot-carousel > div:active {
        transform: scale(0.95);
    }
}

/* Lineup builder responsive adjustments */
@media (max-width: 768px) {
    .lineup-slots-grid {
        grid-template-columns: repeat(2, 1fr) !important;
    }

    .player-grid {
        grid-template-columns: 1fr !important;
    }
}

/* Ensure sticky elements work properly on iOS Safari */
@supports (-webkit-touch-callout: none) {
    .sticky-cta-container {
        padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    }
}
"""

/**
 * Injects raw CSS into a <style> tag in the document head.
 */
private fun injectGlobalCss(css: String) {
    val document = kotlinx.browser.document
    val existingStyle = document.getElementById("global-additional-css")
    if (existingStyle == null) {
        val styleElement = document.createElement("style")
        styleElement.id = "global-additional-css"
        styleElement.textContent = css
        document.head?.appendChild(styleElement)
    }
}

/**
 * Sets up CSS custom property --app-height that accurately reflects the visible viewport height.
 * This fixes the mobile browser issue where 100vh includes the URL bar area.
 * Uses window.innerHeight which gives the actual visible viewport.
 */
private fun setupViewportHeight() {
    val window = kotlinx.browser.window
    val document = kotlinx.browser.document

    fun updateHeight() {
        val vh = window.innerHeight
        document.documentElement?.asDynamic()?.style?.setProperty("--app-height", "${vh}px")
    }

    // Set initial value
    updateHeight()

    // Update on resize (handles orientation change and URL bar show/hide)
    window.addEventListener("resize", { updateHeight() })

    // Also update on orientationchange for mobile devices
    window.addEventListener("orientationchange", {
        // Delay slightly to allow browser to settle
        window.setTimeout({ updateHeight() }, 100)
    })
}
